/**
 * Single source of truth for the public marketing page copy. Consumed by
 * `marketing-site/src/MarketingPage.tsx` (Vite static site).
 *
 * **Edit here** when you change hero, section copy, FAQ, or CTA text—then run
 * `npm run build` in `marketing-site` to verify.
 */

import {
  KYNWARD_MAIN_POSITIONING,
  KYNWARD_MARKETING_CONVERSATION_FIRST,
  KYNWARD_MARKETING_CAMPFIRE_SECTION,
  KYNWARD_MARKETING_CIRCLE_TIMELINE,
  KYNWARD_MARKETING_HERO,
  KYNWARD_MARKETING_TRAIL_SECTION,
  KYNWARD_MARKETING_WHY_SECTION,
  KYNWARD_POSITIONING_ANCHOR,
  KYNWARD_SOCIAL_SETTINGS,
} from '~/constants/kynwardBrandCopy';

export type MarketingFaqItem = { q: string; a: string };

/**
 * Homepage FAQ.
 *
 * Ordered objections-first, then mechanics. It used to open with "What is a Circle?" and read as a
 * product glossary — but by the time someone reaches the FAQ they are not looking up definitions,
 * they are looking for the reason not to sign up. The first five answer that: what makes this
 * different from a group chat, which lane it is for, whether they will be bad at it, what it
 * costs, and what happens if someone behaves badly.
 *
 * The cost answer is the one to be careful with — see the claims block on
 * {@link pricingHonestySection}. Never say "free" flat, never print a price.
 */
export const marketingFaq: readonly MarketingFaqItem[] = [
  {
    q: 'Isn’t this just a group chat?',
    a: 'A group chat is a blank box and a list of names, and somebody has to be brave first. A Circle is four to six nearby people, one shared prompt a day, and a clear ending at the meetup you all choose together. *Nobody has to open cold, carry it, or work out how to leave.*',
  },
  {
    q: 'Is this for dating, or for making friends?',
    a: 'Both — and you don’t have to decide on day one. Circles start as conversation, and what you’re open to helps place you with people looking for the same. Afterwards you privately choose who you’d be glad to hear from again. *Dating can happen; it is never assumed.*',
  },
  {
    q: 'How does Kynward decide who I meet?',
    a: 'Your own answers — the Kynmark quiz, the things you picked as common ground, and what you said you are open to. *There is no desirability score,* nothing is read from your photos, and nothing learns from who you passed on. We are strict the other way too: a question that turns out not to change who you meet gets deleted, not kept for the profile.',
  },
  {
    q: 'What if I’m not good at this?',
    a: 'That is most of the reason Kynward is shaped this way. No blank message box, no opener to get right — everyone answers the same prompt, and a short honest answer is enough. *The prompt goes first, not a blank page.*',
  },
  {
    q: 'What does it cost?',
    a: 'Taking part is free: joining a Circle, answering the prompts, and the conversations that follow. Kynward Plus is a paid subscription that adds room — more Circles at once, more keeps, more invites a day. *It never buys priority:* there are no boosts, and nobody can pay to be placed ahead of you.',
  },
  {
    q: 'What if someone makes me uncomfortable?',
    a: 'Report or block at any point — *you never owe anyone an explanation.* Private chats open only on a mutual choice, and passing stays private. If you meet in person, Kynward checks in with you afterwards, and emails a contact you nominated if you never answer.',
  },
  {
    q: 'Will this actually lead to real connection?',
    a: 'Sometimes, naturally. There is no pressure — but when a Circle feels right, it can turn into something real outside the app, and Kynward is built to hand that over rather than hold on to it.',
  },
  {
    q: 'What is a Circle?',
    a: 'A Circle is a guided group near you, built around the night you pick. It forms a few days before, starts with one low-pressure prompt a day, and ends at a meetup the group chooses — a softer way to see who you naturally click with.',
  },
  {
    q: 'How long does a Circle last?',
    a: 'From when it forms to your meetup — a few days, long enough to feel each other out, short enough that it doesn’t feel like homework.',
  },
  {
    q: 'What happens after that?',
    a: 'You privately choose who you’d be open to hearing from again — Mutuals, private chats, open only when you both choose each other and the connection lane lines up. Keep the whole Circle, and it meets again the next week: same night, a new place.',
  },
  {
    q: 'What if my Circle wants to keep going?',
    a: 'Groups that want to continue can. Kynward supports continued Circles for groups that have more to say, and a kept Circle meets again automatically the next week.',
  },
  {
    q: 'Can I meet people in person through Kynward?',
    a: 'Yes — every Circle already ends in a meetup the group plans together: a coffee, a walk, or a meal, somewhere different each time. You can also meet a Mutual one-to-one, whenever you both want to. After the meet-up starts you get a check-in, and if you never answer it, a contact you nominated is emailed.',
  },
  {
    q: 'Which nights can I pick?',
    a: 'Tuesday trivia, Thursday game night, a Saturday coffee walk, or a Sunday run — each with its own time, or choose no preference and Kynward places you.',
  },
  {
    q: 'Where do we meet up?',
    a: 'Wherever the group picks — there’s no house venue, so it’s somewhere different every time. The plan carries safety details like notice and step-free access as the plan’s needs, never whose.',
  },
  {
    q: 'When can I join, and will it be live in my city?',
    a: 'We’re opening city by city so it doesn’t feel empty when you show up. Kynward isn’t publicly available everywhere yet — join the waitlist below and we’ll email you when we open where you are.',
  },
] as const;

/** In-page scroll targets (ids / nativeIDs) */
export const marketingAnchors = {
  how: 'how',
  /** Problem beat, directly under the hero. */
  problem: 'why-this-is-hard',
  /** Mechanism beat, after the 3-day walkthrough. */
  whyItWorks: 'why-it-works',
  /** Meet-up section — the beat that used to be a third panel inside Beyond the Circle. */
  meetups: 'when-you-meet',
  /** Monetization honesty band, in the trust cluster. */
  pricing: 'how-we-make-money',
  circles: 'circles',
  signal: 'signal',
  /** Signal Fire — the daily 1:1 introduction lane. */
  signalFire: 'signal-fire',
  mutuals: 'mutuals',
  beyondCircle: 'beyond-the-circle',
  openMinded: 'open-minded-circles',
  /** Meet locally (legacy anchor) */
  meetLocally: 'meet-locally',
  different: 'different',
  connection: 'connection-first',
  testimonials: 'testimonials',
  comfort: 'comfort',
  safety: 'safety',
  faq: 'faq',
  download: 'download',
  conversations: 'conversations',
  /** Full-release email waitlist (marketing static site; use /#waitlist in QR codes) */
  waitlist: 'waitlist',
} as const;

/**
 * Header nav labels. These are the words a visitor scans before reading anything, so they name
 * what the reader is looking for, not what the product calls it — "Signal Fire" meant nothing
 * to someone who had not scrolled yet, and "Circles" only slightly more. The lanes
 * stay reachable from the footer's Product column, where a label can sit next to its explanation.
 * Home nav: Why it works (wide widths only) · How it works · Meet-ups · Safety · Questions.
 */
export const marketingNav = {
  theFlow: 'How it works',
  whyItWorks: 'Why it works',
  meetups: 'Meet-ups',
  safety: 'Safety',
  questions: 'Questions',
} as const;

export const marketingCta = {
  downloadApp: 'Download the app',
  downloadKynward: 'Download Kynward',
  seeExampleCircle: 'See example Circle',
  exploreYourCity: 'Explore your city',
  learnMoreSafety: 'Learn more about safety',
  /** Primary hero CTA during private beta. */
  joinBeta: 'Download Kynward',
  notifyFullRelease: 'Download Kynward',
  getLaunchUpdates: 'Download the app',
  tryFirstCircle: 'Download Kynward',
  joinPrivateBeta: 'Download Kynward',
  /** Full-release launch waitlist (POST /api/waitlist/full-release) — not private beta. */
  joinFullReleaseList: 'Join the waitlist',
  joinWaitlistShort: 'Waitlist',
  openApp: 'Open the app',
  /** Beta invite doorway — link under the public waitlist form. */
  inviteLeadIn: 'Have an invite?',
  inviteEnterCode: 'Enter code',
  /** @deprecated Use inviteLeadIn / inviteEnterCode */
  haveInvite: 'I have an invite',
  appPathWithLeadIn: 'Already have an invite? Open the app',
} as const;

export type MarketingDoorId = 'him' | 'her' | 'open';

/**
 * Door campaign hero variants (/for-him, /for-her, /open-minded, ?door=him|her|open). These are
 * EMPHASIS variants of the same product — headline/proof emphasis for campaign traffic, never
 * identity pages (the Open-minded Circles no-assumptions tone binds all of them). Additive
 * exports only: the RN app consumes marketingHero.* (outcomeMessaging.ts), so the default hero is
 * never mutated by door work.
 *
 * Claims vetted against product truth 2026-07-31 (re-vetted after adversarial review) — do not
 * strengthen without re-vetting:
 * - "One at a time" mirrors the shipped signalFireSection chip. The cap is `matchDailyDrawLimit`
 *   = 3 and it is STRICTLY SEQUENTIAL — the next is drawn only once the current one is decided,
 *   because three at once would be the browsable stack that one-a-day existed to prevent. Claim
 *   the sequencing, not the number; "three a day" alone invites exactly the stack reading. The daily
 *   candidate can be null in thin markets, so never escalate to "guaranteed".
 * - "No paid priority" is now TRUE and claimable: the Priority boost IAP and the Plus
 *   "Priority in future drops" benefit were both removed, and the drop queue orders only by
 *   attempts and wait time with no tier term. Earned priority still exists and is not paid —
 *   the quiet-Circle make-good and achievement rewards bump attempt_count — so claim "no paid
 *   priority", never the broader "no priority of any kind".
 * - "Photo-verified profiles" (the feature exists) — never "photo-verified people" as a property
 *   of who's in your Circle: verification is not a Circle-join requirement (matchingReadyGate has
 *   no verification gate).
 * - "No open inbox" / "Nobody starts a thread with you" replaced "No cold DMs" and "Never a
 *   stranger's inbox" on 2026-08-20. The old lines were falsified by the Fireside surface: Signal
 *   Fire renders "{NAME} OPENS THE FIRESIDE" on the dealt daily match, so the daily lane keeps
 *   showing a stranger's opener and the copy must survive that. What IS true: there is no inbox
 *   anyone can write into, no thread a stranger can start, and a private CHAT still needs mutual
 *   choice. Never let these drift back toward "nobody can message you".
 * - Lanes: "chosen, never assumed" — true everywhere. NEVER "you always know the lane": Signal
 *   Fire mutuals open without a continuationLane label.
 * - "Photo-verified profiles" never sits inside a description of the Circle (the her sub used to
 *   read "a small evening Circle — verified profiles"); it stays in trust lines and chips.
 * - Door headlines lead with a mechanic ONLY Kynward has, aimed at that door's pain — never a
 *   generic "safe, no swiping" line any app could print, and never only what is absent (2026-09-23;
 *   "Nobody starts a thread with you" was true but read as "nobody talks to you"). Each door leads
 *   with one lane and its teaser points at the other:
 *   him → Signal Fire (men's pain: friendships that drifted after school and work, not texting
 *         first for fear of seeming needy → one introduction on shared ground, both turns unlock
 *         together, never shown a no); teaser Circles.
 *   her → Circles (women's pain: making friends as an adult is hard, and friend apps feel like
 *         dating apps → see how someone shows up in a group for three days, private picks);
 *         teaser Signal Fire.
 *   open → Open-minded Circles (queer friendship has few places to gather beyond bars; "you're
 *         the default" is true only here — Signal Fire has no Open-minded pool); Signal Fire in
 *         the sub.
 *   Research: docs/CONNECTION_SCIENCE.md and the 2026-07-31 market + LGBTQ+ sweeps.
 * - Every door leads with friendship (2026-09-25, matching the Austin creator cuts). Dating stays
 *   named as something you can choose — "or dating if you choose it", "friends, a date, or both" —
 *   never as the pitch. True on both lanes: Signal Fire only pairs overlapping connection intents
 *   (MatchIntentCompatible) and a both-flexible pair gets the friendship lane; a Circle ends in a
 *   mutual-gated continuation lane.
 * - "Circles form every night" / "Circles nightly" is RETIRED (2026-09-25). A drop opens every
 *   night, but you join the first Circle your city can fill, usually a day or two. Say "drops open
 *   at 7 PM" or "join a Circle". The her door's mutual gate is "a one-to-one chat", since the
 *   Circle is already a group chat. Guarded in marketing-site/src/retiredClaims.test.ts.
 * - The open door speaks to LGBTQ+ people and people still working it out — never to allies, even
 *   though the backend admits them (founder call 2026-09-23). The page is for people who are
 *   different from the norm.
 * - The shared prompt: someone in a Circle still posts first (founder call 2026-09-25). True: everyone
 *   answers the same prompt, so there is always something to reply to and no blank opener to
 *   write from nothing. Never "nobody has to go first" / "nobody's stuck waiting to message".
 * - Open-minded differs by who is in the room, NOT by the prompts: "deeper prompts" was retired
 *   2026-09-05 (see openMindedCirclesSection.pillars) and must not come back in door copy.
 * - The weekly-occasion story (2026-10-01, live, no flag): a visitor picks ONE weekly night — Tue
 *   Trivia 7 PM, Thu Game Night 7 PM, Sat Coffee & a walk 10 AM, Sun Run/walk club 9 AM, or no
 *   preference — and a Circle of four to six nearby people who picked the same night forms 2–3
 *   days before it. Every Circle ends in a meetup the group picks, somewhere different each time;
 *   keeping a Circle carries it into the same night the following week. Claimable: naming the four
 *   nights and their times, "a Circle forms a few days before", "ends in a meetup the group picks".
 *   Never claim a number of occasions per week (Plus may cap it), a guaranteed meetup turnout, or a
 *   fixed/house venue — the venue is chosen fresh by the group each time. This also retires "Circles
 *   form every evening at 7pm" as a HOME claim, not just a door claim: the old `dropRhythmBand` at
 *   #how is replaced by `howItWorksSection` (see `retiredClaims.test.ts`'s three-day pattern).
 */
export const marketingDoorHeroes = {
  him: {
    h1: 'A new friend, one introduction at a time.\nAnd a weekly night to show up to.',
    h1AccentLineIndex: 1,
    sub: 'Signal Fire picks one person for the ground you both said yes to — for friendship, or dating if you choose it, and never for how either of you photographs. They open with one line, you answer before you can read it, and both turns unlock together. Kindle only if you’d keep talking; passing stays private, so you’re never shown a no.\n\nOr pick a night (Tuesday trivia, Thursday game night, a Saturday coffee walk, a Sunday run) and a small Circle forms a few days before. It ends at a meetup the group picks, somewhere new each time.',
    trust: 'No swiping. No desirability score. No paid priority.',
    proofChips: ['Friendship first', 'One at a time', 'Never shown a no', 'Trivia & game nights'],
    note: {
      text: 'Gay, bi, queer? Open-minded Circles are built in — connection without the default assumptions.',
      anchorKey: 'openMinded',
    },
  },
  her: {
    h1: 'New friends, starting with a group.\nGet to know them before you meet.',
    h1AccentLineIndex: 1,
    sub: 'Pick a Saturday coffee walk or a Sunday run, and a Circle of four to six nearby people forms a few days before — the same prompt each day, so you see how everyone shows up before you choose. The group picks the place, somewhere different every time. Picks stay private, a one-to-one chat opens only if it’s mutual, and a Circle you keep meets again the next week.',
    trust: 'No open inbox. Photo-verified profiles. Meetup safety check-ins.',
    proofChips: ['Group first', 'Ends in a meetup', 'Private picks'],
    note: {
      text: 'Lesbian, bi, queer? Open-minded Circles are built in — connection without the default assumptions.',
      anchorKey: 'openMinded',
    },
  },
  open: {
    h1: 'Queer friends, without the bar.\nA Circle where you’re the default.',
    h1AccentLineIndex: 1,
    sub: 'Open-minded Circles are for LGBTQ+ people and anyone still working it out: four to six nearby people around one shared prompt for a few days, so there’s always something to reply to, before meeting up somewhere the group chooses — and not a bar unless you want one. Friends, a date, or both: chosen, never assumed. Signal Fire adds one introduction at a time, picked for the ground you share.',
    trust:
      'No grid. No desirability score. Picks stay private — a chat opens only when it’s mutual.',
    proofChips: ['Built for LGBTQ+ people', 'Group first, not a grid', 'Ends in a meetup'],
    note: null,
  },
} as const;

export type MarketingDoorHero = (typeof marketingDoorHeroes)[MarketingDoorId];

/** Per-door compact pointer band under the hero — an emphasis lever that avoids reordering
 * sections around the Circles scroll-pin. Keys into marketingAnchors for the scroll target. */
export const doorTeasers = {
  him: {
    eyebrow: 'CIRCLES',
    line: 'Between introductions, pick a night and join a Circle: four to six nearby people form a few days before it, ending at a meetup the group picks.',
    cta: 'See how Circles work',
    anchorKey: 'circles',
  },
  her: {
    eyebrow: 'SIGNAL FIRE',
    line: 'Rather meet one person? Signal Fire shows you why you two first, then one real exchange — you both decide after, never before.',
    cta: 'See how Signal Fire works',
    anchorKey: 'signalFire',
  },
  open: {
    eyebrow: 'OPEN-MINDED CIRCLES',
    line: 'Same Circles, same prompts — the difference is who’s in the room. And if the group clicks, keep it — it meets again the next week.',
    cta: 'See Open-minded Circles',
    anchorKey: 'openMinded',
  },
} as const;

export const marketingHero = {
  kicker: '',
  /** Multi-line headline — use newline for split; last line may be accented in UI */
  h1: KYNWARD_MARKETING_HERO.h1,
  h1AccentLineIndex: KYNWARD_MARKETING_HERO.h1AccentLineIndex,
  sub: KYNWARD_MARKETING_HERO.sub,
  trust: KYNWARD_MARKETING_HERO.trust,
  // "No open inbox", not "No cold DMs": the door heroes dropped the DM claim on 2026-08-20 (see the
  // claims block above `marketingDoorHeroes` — a Fireside does put one written line in front of
  // you), and the default hero kept it. These chips also ship as the native welcome door's trust
  // line via WELCOME_DOOR_COPY.
  proofChips: ['No swiping', 'No open inbox', 'Photo-verified profiles'] as const,
  momentEyebrow: 'Today’s prompt',
  momentQuote: 'What’s something small that makes you warm up to someone?',
  momentNote: 'Reply once, see nearby answers — without another endless feed.',
  /** Editorial hero aside — floating product cards over the city-night scene. */
  focusVisual: {
    circleEyebrow: 'Tonight’s Circle',
    circleTitle: '6 people nearby',
    circleBody: 'Prompt opens at 7:00 PM',
    circleFooter: 'Small, prompt-led Circles',
    promptEyebrow: 'Today’s prompt',
    promptTitle: 'What’s something small that makes you warm up to someone?',
    promptBody: 'Everyone starts with the same prompt.',
    mutualsEyebrow: 'Mutuals',
    mutualsTitle: 'Opens when interest is shared',
    mutualsBody: 'Keep talking 1:1 only when it goes both ways.',
  },
} as const;

/**
 * Site-only home hero override (2026-10-01) — marketing-site reads this instead of `marketingHero`
 * on the un-doored "/" route, same shape as `marketingDoorHeroes[door]`. `marketingHero.h1` and
 * `.proofChips` stay untouched above: the native app welcome reads them through
 * `outcomeMessaging.ts`, and this story — pick a weekly night — is marketing-site only so far.
 * `h1AccentLineIndex`/`note` match the `MarketingDoorHero` shape PresenceHero already renders.
 */
export const marketingHomeHero = {
  h1: 'Pick a night. Meet a few people before it.\nKeep the ones you click with.',
  h1AccentLineIndex: 1,
  sub: 'Tuesday trivia, Thursday game night, a Saturday coffee walk, or a Sunday run — pick a night, or no preference. A Circle of four to six nearby people forms a few days before, talks over one shared prompt a day, then meets up somewhere the group picks, a different place each time. Keep the ones you click with and you meet again the next week.',
  trust: 'No swiping. No open inbox. Photo-verified profiles.',
  proofChips: [
    'Tue Trivia · 7 PM',
    'Thu Game Night · 7 PM',
    'Sat Coffee walk · 10 AM',
    'Sun Run club · 9 AM',
  ],
  note: null,
} as const;

/** Outcome clarity — direct follow to hero (before “problem” prose) */
export const outcomeSection = {
  h2: 'Photos matter. Perfection doesn’t.',
  bullets: [
    'Connect through conversation, not profile performance',
    'See who feels easy to talk to over a few days',
    'Kindle the connection only when it’s mutual',
  ] as const,
  bridge:
    'You’re not ranking strangers from a polished bio — you’re building shared context, then choosing who you’d like to hear from.',
} as const;

/**
 * The problem beat — rendered directly under the hero by `ProblemSection`.
 *
 * Replaces the old `makingFriendsProse` + `forYouSection` pair, which were written and then
 * imported by nothing: the page opened straight into product mechanics and never stated what it
 * was fixing, leaving the hero's "No swiping. No cold DMs." to carry the whole argument — a
 * feature list phrased in the negative, which only lands on a reader who already diagnosed
 * themselves in those exact words.
 *
 * The three pains are the validated ones (send-into-the-void, noise-not-scarcity, and the
 * collapsed meet-through-friends pathway). Claims discipline: they describe the EXPERIENCE and
 * never a statistic — a public page that quotes match rates has to defend and keep them current.
 *
 * Asterisk pairs mark the phrase a scanner should catch; see `withEmphasis`. Keep bodies at
 * roughly 25 words — these are scanned, not read, and the first draft ran nearly twice that.
 *
 * `p1Em` is split out separately so web can wrap it in <em> and RN can use a nested <Text>.
 */
export const problemSection = {
  eyebrow: 'WHY THIS FEELS HARD',
  h2: 'If genuine connection has felt harder than it should, you’re not alone.',
  p1Before:
    'Big mixers are loud in the wrong way. Swipe apps make you decide too much from a few photos. And “just put yourself out there” never tells you ',
  p1Em: 'how',
  p1After: '.',
  pains: [
    {
      id: 'void',
      title: 'You send, and nothing comes back.',
      body: 'You write first every time, rework the opener, and mostly hear nothing. After enough of that it *stops feeling like bad luck and starts feeling like a verdict.*',
    },
    {
      id: 'noise',
      title: 'Or it comes back, and none of it is worth reading.',
      body: 'Messages from people who clearly never read a word you wrote. The real work isn’t finding someone — it’s *sorting, filtering, and bracing yourself.*',
    },
    {
      id: 'friends',
      title: 'Nobody warns you that making friends got hard too.',
      body: 'The people you’d once have met through other people — a roommate’s coworker, someone’s Saturday group — *you just don’t anymore.*',
    },
  ] as const,
  bridgeLead: 'Kynward is built for all three.',
  bridge:
    'Small groups of nearby people, one prompt everyone answers, and *nobody starts from a blank box.*',
  forYouEyebrow: 'This is for you if',
  forYou: [
    'You’re new to the city',
    'You’re tired of swipe-app pressure',
    'You don’t like big social events',
    'You want connection without performing',
    'You’re open to friendship, dating, or something in between',
  ] as const,
} as const;

/**
 * The "why it works" beat — rendered after the 3-day Circle walkthrough by `WhyItWorksSection`.
 *
 * The page had plenty of "how" (day 1/2/3, settings, activities) and no "why": every mechanic is
 * designed against a specific failure, and the copy stated the mechanic and stopped. Each row here
 * names the mechanic AND the failure it removes, with asterisk pairs on the half that has to land
 * if the reader only skims one clause.
 *
 * Deliberately no citations and no "studies show" — `docs/CONNECTION_SCIENCE.md` §6 lists claims
 * from this literature that failed replication, so the copy states the human mechanism in plain
 * terms and stakes nothing on a paper. Every mechanic named here is shipped: the group itself, the
 * shared prompt (Circle day 1), the blind Fireside exchange (`signalFireSection.steps`), private
 * picks (`KYNWARD_MUTUALS_CHOOSE_INTRO`), and one-a-day with a three-day close.
 *
 * The `yourAnswers` row and `inputs` are the ones to re-check before editing. Re-derived from the
 * scorers 2026-10-02 (kynward-backend 3af328b6), because CONNECTION_SCIENCE.md §13a-bis says the
 * doc's weight table is stale:
 * - Signal Fire (`match_of_the_day_job.go`) blends Circle Style, commonality ground and interests,
 *   plus lane preference, partner and lifestyle deltas; `connectionIntent` is a hard gate there
 *   (`MatchIntentCompatible`). In Circles it is only a soft nudge plus a group-level floor, so the
 *   intent line must never promise that everyone in a Circle wants what you want.
 * - Circle formation (`PairScoreWithoutFeedbackAndSoft`) sums ~20 terms. Most are fields a member
 *   filled in, but NOT all: `PairBehavioralOverlay` reads observed Circle conduct (reliability,
 *   ghosting, presence, reply rhythm from `UserProfileV1`). An earlier version of this comment said
 *   every term was user-authored and the row read "Your answers are the input"; that was wrong.
 * - Excluded and pinned: no physical attribute (`TestMotdRomanceBonus_IgnoresPhysicalAttributes`;
 *   height was removed, §13h), nothing photo-derived, no learner over pass/kindle decisions, and
 *   nothing for sale buys placement (see `pricingHonestySection`).
 *
 * Why the page lists inputs at all (research pass 2026-10-02): users' loudest algorithm complaint is
 * that matching serves the paywall, so saying what goes in and what stays out is a differentiator.
 * Why it never claims accuracy: no matcher predicts who clicks (§14), and an over-promise produces
 * the disappointment that turns into distrust. The promise is people worth talking to and a better
 * first conversation, never a perfect match.
 *
 * What must NOT be claimed from it: that answering more questions gets you BETTER matches. §13c is
 * explicit — stated preferences are weak predictors of attraction, so filters are a comfort lever
 * rather than a matching improvement, and copy must never defend one as making matches better.
 * This claim is about what the score is MADE OF. It says nothing about what the score delivers,
 * and it must stay that way.
 */
export const whyItWorksSection = {
  eyebrow: 'WHY IT WORKS',
  h2: KYNWARD_MARKETING_CONVERSATION_FIRST.h2,
  lead: KYNWARD_MARKETING_CONVERSATION_FIRST.lead,
  items: [
    {
      id: 'group',
      mechanic: 'You meet people in a group, not one on one.',
      because:
        'Because that is how friendships used to start — through other people, in a room you were already in. *A Circle puts the room back.*',
    },
    {
      id: 'yourAnswers',
      mechanic: 'You’re matched on what you tell us and how you show up — not your face.',
      because:
        'Because the alternative is being ranked by your photos, by who swiped you, by a desirability score. *Kynward keeps none of those.* The full list is below.',
    },
    {
      id: 'sharedPrompt',
      mechanic: 'Everyone answers the same prompt.',
      because:
        'Because someone always has to go first, and elsewhere it is *the same people every time, until they quit.* Here, going first means answering a prompt everyone shares — not writing a cold opener.',
    },
    {
      id: 'blindExchange',
      mechanic: 'You answer before you can read their answer.',
      because:
        'Because once you can see theirs, you play off it — and *one person ends up carrying the whole conversation.* Both turns unlock together.',
    },
    {
      id: 'privatePicks',
      mechanic: 'Your picks stay private.',
      because:
        'Because what stops people isn’t indifference, it’s *the risk of being the only one who said yes.* Nobody is ever shown a no.',
    },
    {
      id: 'oneAndDone',
      mechanic: 'You meet one person at a time.',
      because:
        'Because a stack to browse turns you into *someone ranking people* instead of someone in a conversation. The next one is lit only once you’ve decided on this one.',
    },
  ] as const,
  /** What matching reads and what it never reads — see the header for the code each line rests on. */
  inputs: {
    title: 'What we match on',
    goesInLabel: 'What goes in',
    goesIn: [
      {
        id: 'intent',
        label: 'What you’re here for',
        detail:
          'Friends, dating, or both. A one-to-one introduction only happens when you want the same.',
      },
      {
        id: 'whereWhen',
        label: 'Where and when',
        detail: 'People near you and, for a Circle, free on the same nights.',
      },
      {
        id: 'circleStyle',
        label: 'How you like a group to feel',
        detail: 'Your Circle Style — the kind of company you said you enjoy.',
      },
      {
        id: 'ground',
        label: 'Ground you share',
        detail: 'The specific things you both picked, not just broad interests.',
      },
      {
        id: 'lifestyle',
        label: 'How you like to spend an evening',
        detail: 'Drinks or not, budget, how long you like to stay out, life stage.',
      },
      {
        id: 'showUp',
        label: 'How you show up',
        detail: 'In Circles, people who reply and turn up are grouped with people who do the same.',
      },
    ],
    neverInLabel: 'What never does',
    neverIn: [
      'Your photos, or how you look',
      'Who swiped on you, or any desirability score',
      'Money — paying never puts you in front of anyone',
    ],
    promise:
      'No app can predict chemistry, and we don’t pretend to. This gets you to people you’d *actually want to talk to* — the rest is about making that first conversation easy.',
  },
  // No count in this line on purpose — "the four things" went stale the moment a fifth item was
  // added, the same way the why-card titles drifted from their icon matcher.
  closer: 'None of this manufactures a connection. It *removes what usually stops one.*',
} as const;

/**
 * Monetization honesty — rendered in the trust cluster by `PricingHonestySection`.
 *
 * The incentive complaint is one of the loudest and best-evidenced pains in this market (an app
 * that earns per month-unmatched has a reason to keep you unmatched), and the page said nothing
 * about money at all. Silence there reads as evasion to exactly the reader who left another app
 * over it.
 *
 * CLAIMS — checked against the shipped product 2026-08-19, do not strengthen without re-checking:
 * - "No boosts" / "no paid priority" is the vetted claim (see the block above
 *   `marketingDoorHeroes`): the Priority boost IAP and the Plus "Priority in future drops" benefit
 *   were both removed, and the drop queue orders on attempts and wait time with no tier term.
 *   Earned priority still exists and is not paid.
 * - Plus benefits are CAPACITY, never rank: more concurrent Circles, `PLUS_KEEP_LIMIT` (5 vs 3)
 *   people kept per Circle, unlimited daily Kindles (vs `FREE_KINDLES_PER_DAY`), more sparks and
 *   replies. Nothing in `MONETIZATION_CATALOG_USD_CENTS` sells visibility.
 * - NEVER claim "free", "no paywalls", or "everything that decides who you meet is free". Plus is
 *   a real subscription, consumables exist, and some matching preferences sit behind Plus. The
 *   defensible line is narrow and specific: you cannot buy your way in front of another person.
 * - NEVER print a price here. The catalog is the source of truth and store pricing varies.
 */
export const pricingHonestySection = {
  eyebrow: 'HOW WE MAKE MONEY',
  h2: 'You can’t buy your way in front of anyone.',
  lead: 'The apps people leave tend to earn more the longer nobody works out. That shapes every screen they build. Here is ours.',
  items: [
    {
      id: 'noBoost',
      title: 'There are no boosts.',
      body: 'Nothing here puts your profile ahead of someone else’s, because *no such thing is for sale.*',
    },
    {
      id: 'queue',
      title: 'Your place in line isn’t for sale.',
      body: 'Waiting time counts, and so does how many times you’ve tried. *What you’ve paid never does — there is no tier that skips the line.*',
    },
    {
      id: 'plus',
      title: 'Plus buys room, not rank.',
      body: 'More Circles at once, more people you can keep, more invites a day. *Space to use, never an advantage over anyone.*',
    },
  ] as const,
  closer: 'Nothing here works better when you fail. *That was the point.*',
} as const;

/** Short labels for the rhythm trail / preview (“Day n of 3”) — main path is Circles only. */
export const rhythmStepShortLabels = [
  'Start in a small Circle',
  'Talk with context',
  'Choose what continues',
] as const;

/** Scroll-linked rhythm bar hint (marketing site) */
export const rhythmScrollHint =
  'Keep scrolling — the KYN trail lights up as you move through each step.';

/** Brand meaning — after hero, before “how it works”. */
export const whyKynwardSection = KYNWARD_MARKETING_WHY_SECTION;

/** Campfire vs feed — emotional product frame. */
export const campfireSection = KYNWARD_MARKETING_CAMPFIRE_SECTION;

/**
 * Rotating social settings — honest launch framing (Campfire first).
 *
 * Rendered by the marketing homepage "Inside each Circle" section. The ladder teases the
 * roadmap under the brand rule in {@link KYNWARD_SOCIAL_SETTINGS}: only Campfire reads as live,
 * Game Night is the single "next", Studio + Observatory sit "on the horizon" — never "shipping".
 * Marketing-only: keep the coming-soon ladder out of in-app copy in kynwardBrandCopy.ts.
 */
export const socialSettingsSection = {
  h2: KYNWARD_SOCIAL_SETTINGS.sectionHeadline,
  body: KYNWARD_SOCIAL_SETTINGS.sectionBody,
  bodyShort: KYNWARD_SOCIAL_SETTINGS.sectionBodyShort,
  oneLiner: KYNWARD_SOCIAL_SETTINGS.oneLiner,

  // Rendered section copy.
  eyebrow: KYNWARD_SOCIAL_SETTINGS.sectionHeadline, // "Inside each Circle"
  title: 'Every Circle has a setting.',
  lead: 'A setting shapes the mood, the prompts, and the small activities in the days before you meet.',
  reframe:
    '*The night you pick sets it.* Trivia and game nights get Game Night; a coffee walk or run club gets Campfire.',
  /**
   * The two venues that actually exist, described by what a Circle actually plays in them.
   *
   * Every activity line here is the product's own copy, lifted from `getActivityCardDescription`
   * and `CIRCLE_ACTIVITY_COPY` rather than written for the site — marketing that paraphrases a
   * feature drifts from it the first time the feature changes, and this section was already proof
   * of that: it promised Game Night would bring "two truths, quick rounds, and small dares", none
   * of which were ever built, while listing "Side Trail" among Campfire's activities when Side
   * Trail is a THREAD, not a game.
   *
   * Two activities per venue, not the full roster. Eight named games across two cards was the
   * densest block of product nouns on a page that already asks a cold reader to learn Circle,
   * Drop, Wrap, Mutual, Kindle and Fireside; the section's job is to show that a setting changes
   * what a Circle does, and two examples do that. Kept per venue: the one whose mechanic reads in
   * a sentence (a blind pick that reveals together; a card into a blank that turns face up when
   * the table is in) and the one that shows the arc or the warmth (a song with a note; a story
   * across two nights). Pack List, Build the Fire, The Vault and The Gauntlet are still in the
   * product — they are just not on the homepage.
   */
  venues: [
    {
      id: 'campfire',
      glyph: 'flame',
      temperature: 'Warmth',
      label: KYNWARD_SOCIAL_SETTINGS.circleHeaderLabel, // "Setting: Campfire"
      status: 'Live now',
      title: KYNWARD_SOCIAL_SETTINGS.circleInfoTitle, // "Campfire"
      body: "Warm, low-pressure, and built around daily prompts, activities, and side conversations — the easiest first reply you'll send on any app.",
      threads: 'Campfire is the main thread. Side Trail is for lighter follow-ups and tangents.',
      activities: [
        {
          when: 'Night two',
          name: 'Common Ground Map',
          body: 'Pick up to 3, blind. The reveal shows who picked the same.',
        },
        {
          when: 'In rotation',
          name: 'Campfire Playlist',
          body: 'Add one song. The note about why is the part people read.',
        },
      ],
      promptLabel: 'Tonight around the fire',
      prompt: 'What would you want a new friend to know about you?',
      repliesNote: '4 nearby people, easing in',
    },
    {
      id: 'gameNight',
      glyph: 'dice',
      temperature: 'Play',
      label: 'Setting: Game Night',
      status: 'Live now',
      title: 'Game Night',
      body: 'A game each night, snacks in the kitchen, and no rush about any of it. Play lowers the guard faster than conversation does, so the talking starts sideways and gets there anyway.',
      threads: 'The Table is the main thread. Snack Table is for lighter follow-ups and tangents.',
      activities: [
        {
          when: 'Night one',
          name: 'Wild Card',
          body: 'Play a card into the blank. Nothing turns face up until the whole table is in.',
        },
        {
          when: 'Nights two and three',
          name: 'The Delve',
          body: 'The scene is set. Say what you do — one story across two nights, so the last one has a history to end on.',
        },
      ],
      promptLabel: 'Tonight at the table',
      prompt: 'The fastest way to lose a group chat: ______',
      repliesNote: '4 nearby people, holding a hand',
    },
  ] as const,

  // Setting ladder — one world, four social temperatures. Order = Play → Warmth → Make → Wonder.
  ladderEyebrow: 'The settings',
  ladderTitle: 'Four ways a Circle can feel.',
  ladderLead:
    'Each setting is a different social temperature — one world, under the same night sky.',
  arc: ['Play', 'Warmth', 'Make', 'Wonder'] as const,
  ladder: [
    {
      id: 'gameNight',
      glyph: 'dice',
      temperature: 'Play',
      title: 'Game Night',
      body: 'Cards, a heist puzzle, and a campaign that runs across two nights. Play first; the talking follows.',
      status: 'Live now',
      state: 'live',
    },
    {
      id: 'campfire',
      glyph: 'flame',
      temperature: 'Warmth',
      title: 'Campfire',
      body: 'Stories and trust around the daily prompt. Warm, low-pressure, built for easy first replies.',
      status: 'Live now',
      state: 'live',
    },
    {
      id: 'studio',
      glyph: 'spark',
      temperature: 'Make',
      title: 'Studio',
      body: 'Build something together — a playlist, a plan, a shared idea the Circle leaves with.',
      status: 'On the horizon',
      state: 'horizon',
    },
    {
      id: 'observatory',
      glyph: 'night',
      temperature: 'Wonder',
      title: 'Observatory',
      body: 'Trade dreams and what-ifs, once your Circle has clicked and the guard is down.',
      status: 'On the horizon',
      state: 'horizon',
    },
  ] as const,
  tease: {
    eyebrow: 'The next setting',
    glyph: 'spark',
    title: 'Studio is being built.',
    body: 'Make something instead of talking about making something — a playlist, a plan, an idea the Circle leaves with. The next character a Circle can take on, once Play and Warmth have both had their run.',
    soon: 'On the horizon',
  },
} as const;

export const productStorySection = {
  h2: 'Designed for real connection',
  body: '',
  chips: [] as const,
} as const;

export const realConnectionFeatures = {
  h2: 'Designed for real connection',
  items: [
    {
      title: 'Right-sized by design',
      body: 'Circles stay limited to six people so everyone has room to show up.',
      glyph: 'circle' as const,
    },
    {
      title: 'A few days together',
      body: 'Enough time to move past surface-level chat — with a clear, calm ending.',
      glyph: 'clock' as const,
    },
    {
      title: 'Prompts, not noise',
      body: 'Prompts give the group something real to respond to.',
      glyph: 'spark' as const,
    },
    {
      title: 'Mutuals you trust',
      body: 'Private chats open only when you both choose each other and the lane lines up.',
      glyph: 'lock' as const,
    },
  ] as const,
} as const;

/** Everyday Circles — broad local matching (also used in signup/tooltips). */
export const commonCirclesSection = {
  shortLabel: 'Broad local Circles for conversation-first connection.',
  body: 'Meet nearby people through low-pressure group conversation. Friendship, dating, or both can continue at Mutuals — only when it’s mutual.',
} as const;

/** One homepage band — optional fluid Circle style, not the default product. */
export const openMindedCirclesSection = {
  h2: 'Open-minded Circles',
  lead: 'Built for LGBTQ+ people and anyone still working it out — small evening groups in your city.',
  paragraphs: [
    'Open-minded Circles are for LGBTQ+ people and those still figuring things out. Same conversation, friendship, and dating as Everyday Circles \u2014 in a room where you are not the only one.',
  ] as const,
  /**
   * The pillars describe the ROOM, not the prompts. Open-minded Circles run the same prompts as
   * Everyday Circles (one spine, two voices); what differs is who is in the room. The old pillars
   * promised "relationship styles" and "deeper prompts", which is the assumption the prompts were
   * then written to deliver — retired 2026-09-05 with the Day 1 catalog rewrite.
   */
  pillars: [
    { label: "You're the default", glyph: 'people' },
    { label: 'Friendship and dating', glyph: 'heart' },
    { label: 'Nothing to explain', glyph: 'orbit' },
    { label: 'Prompts about you', glyph: 'prompts' },
  ] as const,
} as const;

/** Shorter inclusive copy for signup cards / tooltips (not repeated on homepage). */
export const openMindedCirclesSignupCard = {
  h3: 'Open-minded Circles',
  body: 'Built for LGBTQ+ people and those still figuring things out \u2014 the same conversation, friendship, and dating as Everyday Circles, in a room where you are not the only one.',
} as const;

export const circlesSection = KYNWARD_MARKETING_CIRCLE_TIMELINE;

/** @deprecated Use {@link circlesSection.timeline} */
export const circlesSectionLegacy = {
  h2Lines: [KYNWARD_MARKETING_CIRCLE_TIMELINE.h2] as const,
  metaLine: '4\u20136 people \u00b7 3 days \u00b7 prompts \u00b7 activities \u00b7 Mutuals',
  intro: '',
  closingNote: '',
  boardTitle: "Today's Circle",
  settingLabel: 'Campfire',
  boardPrompt: KYNWARD_MARKETING_CIRCLE_TIMELINE.previews[1].prompt,
  promptHelper: KYNWARD_MARKETING_CIRCLE_TIMELINE.previews[1].helper,
  activityChips: ['Common Ground Map', 'Build the Fire'] as const,
  trailPreview: 'Quick thread for weekend plans',
  features: [
    {
      id: 'campfire',
      eyebrow: 'CONVERSATION SETTING · CAMPFIRE',
      title: 'Campfire',
      body: 'The first conversation setting — warm, low-pressure, and built around the daily prompt everyone shares.',
      visual: 'campfire',
      promptSample: 'What would you want a new friend to know about you?',
    },
    {
      id: 'activities',
      eyebrow: 'ACTIVITIES',
      title: 'Activities',
      body: 'Small moments that give the group something to respond to.',
      visual: 'activities',
      activityChips: ['Common Ground Map', 'Build the Fire', 'Group picks'] as const,
    },
    {
      id: 'sideTrail',
      eyebrow: 'SIDE TRAIL',
      title: 'Side Trail',
      body: 'A quieter branch for side conversations without taking over the whole Circle.',
      visual: 'sideTrail',
      trailPreview: 'Quick thread for weekend plans',
    },
  ] as const,
} as const;

/**
 * Signal Fire — the daily 1:1 lane that runs alongside Circles. Mirrors the in-app ritual in
 * `~/components/match/MatchHubScreen`: introductions met one at a time, chosen on shared ground; a blind
 * Fireside exchange; then a mutual Kindle by midnight opens a 1:1 in Mutuals. Keep the copy honest
 * about the gate (a Fireside is not a match) and about passing staying private.
 *
 * Rekindle (the Plus ask to bring a fizzled Signal Fire back) is deliberately NOT marketed here —
 * the homepage sells the free daily ritual, not the paywall.
 */
export const signalFireSection = {
  eyebrow: 'SIGNAL FIRE',
  // Non-breaking space before the dash: at phone widths the first line wrapped after "time" and
  // left the em dash alone on a line of its own.
  h2: 'One introduction at a time —\nand a conversation before either of you decides.',
  lead: 'Circles are the group. Signal Fire is the other way in: one person at a time, chosen for the ground you already share, with a short exchange before anyone has to commit to anything.',
  steps: [
    {
      id: 'why',
      label: 'Why you two',
      body: 'No stack of strangers to rank. You see the constellation first — the things you both actually said yes to — and how each of you tends to show up in conversation.',
    },
    {
      id: 'fireside',
      label: 'The Fireside',
      body: 'They open with one line. You answer before you can read theirs, so neither of you is playing off the other. Both turns unlock together.',
    },
    {
      id: 'kindle',
      label: 'Then you decide',
      body: 'Kindle only if you’d genuinely keep talking. If you both do before midnight, a 1:1 opens in Mutuals with the whole Fireside already inside.',
    },
  ] as const,
  footnote:
    'Passing stays private — they’re never shown a no. One at a time: the next is lit only once you’ve decided on this one.',
  chips: ['No swiping', 'One at a time', 'Constellation first'] as const,
  preview: {
    ritual: 'One introduction, chosen for you',
    /** Kynmark motives — keep these to real keys from `CIRCLE_MOTIVE_KEYS`. */
    youLabel: 'YOU · CURIOSITY',
    themLabel: 'JORDAN · WARMTH',
    sharedCount: '4 shared',
    sharedNote: 'more when you connect',
    sharedStars: ['Live music', 'Long walks', 'Cooking'] as const,
    openerLabel: 'JORDAN OPENS THE FIRESIDE',
    opener: 'What’s something you’ve gotten unreasonably into lately?',
    yourTurnLabel: 'YOUR TURN',
    yourTurn: 'Your turn…',
    ctaLabel: 'Pull up a chair',
    ctaSub: 'A fireside isn’t a match — you decide after',
  },
} as const;

/**
 * Mutuals has no section of its own any more, and no copy export either.
 *
 * It was a panel under "The Circle is only the start" saying "you privately choose what you'd be
 * open to, a private chat opens only when you both choose each other" — one screen after the Circle
 * walkthrough's "When it ends" group said the same thing over the same screenshots. The walkthrough
 * is the right place for it: Mutuals is what a Circle ends in, not another way to start one. Its
 * words now live in `KYNWARD_MARKETING_CIRCLE_TIMELINE.shotGroups`.
 *
 * Deleted rather than deprecated in place, for the reason given below `meetLocallySection`: an
 * unrendered copy export is exactly what leaves this page describing things it never shows.
 */

/**
 * The meet-up beat — its own section, rendered by `MeetupSection`.
 *
 * (Replaces `irlMeetupPanel`, which is deleted rather than deprecated in place: an unrendered copy
 * export is exactly what left this page with no problem statement for as long as it did.)
 *
 * Was a third panel inside `BeyondTheCircleSection`, where it sat alone on a second row of a
 * two-column grid and read as an afterthought; the founder's read of the page was that meet-ups
 * were not mentioned at all. They are the point of the product, so they get a section. Removing
 * it from that grid also leaves a clean 2-up there instead of an orphan.
 *
 * CLAIMS — checked against `MeetupProposeFlow.tsx` and `ModelsMeetupPlanningHints` 2026-08-19:
 * - Propose a coffee / walk / meal, safety contact, check-in reminder: all shipped.
 * - The notice hint is GROUP-scoped and renders above the day picker so it shapes the choice
 *   rather than explaining one already made. It is phrased as what the group needs and never who
 *   needs it \u2014 naming the member with the least flexible week would out them, and a test pins
 *   that. Say "the group", never a person.
 * - It only appears when the group actually answered (`noticeAnsweredBy`) and the need exceeds a
 *   day; the server withholds the hints rather than sending a default the client would present as
 *   the group's preference. The copy says so, because the restraint is the trustworthy part.
 * - The age rule is enforced where the venue BECOMES the plan, not just in the recommendation
 *   list: PostMeetup and PostMeetupOption both run `venueExcludedByAge` and refuse with 400
 *   `venue_requires_age_21`, before any row is written, failing closed on unknown ages. It resolves
 *   a Circle to its members and a 1:1 to both participants, so "the group" is right for either.
 *   Backend `694633f`, pinned by one regression test covering both endpoints deliberately.
 * - The lifestyle answers DO steer where a plan lands, through the venue matcher, and all of it is
 *   ungated. An earlier pass claimed the opposite after finding only DrinkSettingPairDelta and
 *   MeetupKindsPairDelta, which are gated — it never checked handlers/partner_venue_match.go.
 *   venueMatchContext carries four signals, and their failure directions are the interesting part:
 *     * drink setting -> needsSoberSpace. FAIL-CLOSED: an unreadable answer still counts as
 *       needing one, because "the promise was made in the quiz; a failed read is not a reason to
 *       break it". Alcohol-centred venues are withheld.
 *     * age -> hasUnder21. FAIL-CLOSED, and also enforced on the write endpoints (backend 694633f).
 *     * spend band -> tightestSpendBand. CODE-TRUE AND DATA-EMPTY, so it is claimed NOWHERE.
 *       venueExcludedByPrice ships and is tested, but migration 000033 added partner_venues.
 *       price_band as VARCHAR(16) NULL with no default, and the only writer is the partner
 *       application form. Every existing row is NULL, so the filter excludes nothing, and
 *       MeetupPlanningHints carries no budget field either — the group budget reaches neither the
 *       venue list nor the human picking. A claim can be false two ways: the code is off, or the
 *       table is empty. The second is the more dangerous one, because a feature flag announces
 *       itself while an empty column does not — the path runs, the tests pass, and nothing
 *       happens. BAR FOR ANY CLAIM ON THIS PAGE: a reader AND data.
 *     * meet-up kinds -> acceptedKinds, which RANKS only and must never exclude, since the
 *       intersection across four people can legitimately be empty.
 * - Still gated, still unclaimable: lifestyle MATCHING (who you are grouped with) sits behind
 *   LifestyleScoringEnabled() / LIFESTYLE_SCORING_ENABLED, set in no environment, deliberately,
 *   pending calibration data. "We keep you out of Circles whose plans run expensive" stays off
 *   this page. Steering a PLAN and choosing a GROUP are different claims; do not merge them.
 */
export const meetupSection = {
  eyebrow: 'EVERY CIRCLE ENDS IN A MEETUP',
  h2: 'The group picks the place. It’s somewhere new every time.',
  lead: 'No house bar, no fixed venue — Kynward plans the meet-up with you, including the two things that quietly kill most of them.',
  items: [
    {
      id: 'propose',
      title: 'The night is set. The only question is where.',
      body: 'The group picks a coffee, a walk, or a meal from inside the chat. *Nobody has to raise the idea from nowhere.*',
    },
    {
      id: 'notice',
      title: 'Nobody has to explain themselves.',
      body: 'The plan carries what it needs — notice, somewhere you can hear each other, dietary options, step-free access — *as the plan’s needs, never whose.*',
    },
    {
      id: 'safety',
      title: 'Sorted before you go.',
      body: 'A check-in after you were due to meet, someone we email if you miss it, and *a venue nobody in the group is too young for — or asked to avoid.*',
    },
  ] as const,
  venueChips: ['Coffee', 'Walk', 'Meal'] as const,
  safetyRowLabel: 'Safety contact added',
  safetyNote: 'Safety contact · Check-in reminder · No pressure',
  closer:
    'Every one of those came from a question somebody answered once — and *nobody has to say it out loud again.*',
} as const;

/**
 * The umbrella over the lane that is not Circles: Signal Fire.
 *
 * The intro used to explain Mutuals instead. It no longer does, because Mutuals is not a way in —
 * it is where every lane arrives, and the Circle walkthrough already covers it under "When it
 * ends". Two doors open a Mutual: private picks at a Circle's Wrap, and a mutual Kindle on a
 * Signal Fire, which runs through a Fireside first — which is why the line below says the same
 * door rather than the same thing.
 */
export const beyondTheCircleSection = {
  title: 'The Circle is only the start',
  intro:
    'Circles are where it starts — but they are not the only way in. A Signal Fire brings you *one introduction at a time*, every day. Both open the same door a Circle does.',
} as const;

export const meetLocallySection = {
  eyebrow: 'MEET LOCALLY',
  h2: 'Your city.\nYour kin.\nOpen-minded\nby design.',
  body: 'Kynward helps you build real connection nearby through Circles shaped around openness, intent, and mutual interest.\n\nFor LGBTQ+ people and anyone still working it out — with fewer assumptions about who connection is for.',
  bullets: ['Local Circles', 'Open-minded Circles', 'No default assumptions'] as const,
  cta: marketingCta.exploreYourCity,
  mapCity: 'Austin',
  scenePhotos: ['Evening at the bar', 'Dinner with friends', 'City lights'] as const,
} as const;

export const thoughtfulConversationsSection = {
  h2: 'Thoughtful conversations that\ngo beyond the usual openers.',
  screens: [
    {
      label: 'Daily prompt',
      title: 'Today’s prompt',
      body: 'What’s an adventure you’d love to have?',
    },
    {
      label: 'Reactions',
      title: 'Circle reactions',
      body: 'See how others answered — react, reply, notice who feels easy.',
    },
    {
      label: 'Kindle',
      title: 'Who would you like to connect with?',
      body: 'Choose who you’d be open to hearing from after the Circle.',
    },
    {
      label: 'Match',
      title: 'It’s a match!',
      body: 'When you both kindle each other, you can talk privately.',
    },
  ] as const,
} as const;

/** Hero / CTA social proof — replace count when live waitlist data is available. */
export const marketingWaitlistSocialProof = 'Be among the first in your city';

/**
 * Product proof — not named user testimonials (live-safe).
 *
 * Each item leads with the thing that goes wrong elsewhere, then what replaces it. They used to
 * be feature restatements ("Something real to respond to"), which made this section a second
 * feature list rather than the payoff of the problem beat at the top of the page.
 *
 * `id` is load-bearing: `TestimonialsSection`'s icon picker keys off it. It previously matched on
 * `title.startsWith(...)`, so rewording a title silently dropped every card to the fallback
 * checkmark — exactly what this rewrite would have done.
 */
export const testimonialsSection = {
  h2: 'Why Kynward feels different',
  lead: 'Five things that go wrong everywhere else — and what stands in their place here.',
  items: [
    {
      id: 'prompt',
      title: 'No blank-message paralysis',
      body: 'Every Circle opens on a prompt you all answer, so nobody sits staring at an empty box trying to sound interesting.',
    },
    {
      id: 'smallGroup',
      title: 'No room full of strangers',
      body: 'Four to six nearby people over a few days. Small enough that showing up costs you nothing.',
    },
    {
      // Was "Streaks and milestones stay with you". Streaks are the one thing this product's own
      // thesis says not to sell (CONNECTION_SCIENCE.md §4b: extrinsic reward schedules make a Circle
      // less fun, not more), and to the burnt-out reader this page is written for, "streaks" is the
      // slot machine they just left. What this card should carry is the friendship outcome nothing
      // else on the page states plainly: a group that clicks does not have to end (continued
      // Circles are real — `wrapUpChoiceProgress.ts`, `keepCandidates.ts`).
      id: 'history',
      title: 'No “that was nice” and then nothing',
      body: 'A Circle that clicks can keep meeting the next week — same night, new place. And when two people you circled with become Mutuals, you’ll know — most apps never tell you that you helped.',
    },
    {
      id: 'mutual',
      title: 'No rejection to sit through',
      body: 'Friendship or dating continues only when both people choose each other and the lane lines up. Passing is private — nobody is ever handed a no.',
    },
    {
      id: 'offline',
      title: 'No endless texting that goes nowhere',
      body: 'Every Circle already ends in a meetup the group picks. Add a safety contact. Get a check-in reminder. The path from first prompt to first coffee is built in.',
    },
  ] as const,
} as const;

export const rhythmSection = {
  h2: KYNWARD_MARKETING_TRAIL_SECTION.h2,
  lead: KYNWARD_MARKETING_TRAIL_SECTION.lead,
  steps: KYNWARD_MARKETING_TRAIL_SECTION.steps,
  closer: '',
  dayStates: [
    { day: 1, label: 'Start easy', short: 'Start easy' },
    { day: 2, label: 'Add the next layer', short: 'Go deeper' },
    { day: 3, label: 'Notice what stuck', short: 'Choose kindles' },
  ] as const,
} as const;

/**
 * Replaces `dropRhythmSection` / `DropRhythmBand` (2026-10-01) — "Every evening at 7pm, Circles
 * form" contradicted the weekly-occasion story: a visitor now picks ONE of four weekly nights (or
 * no preference), not whichever nightly drop fills next. Rendered by `HowItWorksBand` at #how.
 */
export const howItWorksSection = {
  eyebrow: 'HOW IT WORKS',
  h2: 'Pick a night. Meet the people before it.',
  lead: 'Four weekly nights, each with its own Setting — or let Kynward place you if you’re not sure yet.',
  steps: [
    {
      id: 'pick',
      title: 'Pick your night',
      body: 'You pick the plan; *nobody picks you from a grid.*',
    },
    {
      id: 'forms',
      title: 'A Circle forms before it',
      body: '2–3 days ahead, four to six nearby people who picked the same night get grouped into a Circle. One prompt a day, so there’s always something to reply to.',
    },
    {
      id: 'meetup',
      title: 'Every Circle ends in a meetup',
      body: 'There’s no house venue. *The group chooses where to go, and it’s somewhere different each time.*',
    },
    {
      id: 'keep',
      title: 'Keep the ones you clicked with',
      body: 'Picks stay private, and a 1:1 opens only if it’s mutual. Keep the Circle and *it meets again the next week: same night, new place.*',
    },
  ] as const,
  /** The four weekly nights a visitor can pick, in the order copy always lists them. */
  nights: [
    { day: 'Tuesday', label: 'Trivia', time: '7 PM' },
    { day: 'Thursday', label: 'Game Night', time: '7 PM' },
    { day: 'Saturday', label: 'Coffee & a walk', time: '10 AM' },
    { day: 'Sunday', label: 'Run/walk club', time: '9 AM' },
  ] as const,
  noPreference: 'Not sure yet? Choose no preference and Kynward places you.',
} as const;

export type WhyBeliefGlyph = 'noSwipe' | 'noDm' | 'noCrowd' | 'mutual';

export const whyLighter = {
  h2: 'Why it feels different',
  cards: [
    {
      title: 'Something real to respond to',
      body: 'You begin with shared prompts, so there’s already something real to respond to.',
      glyph: 'noSwipe' as const,
    },
    {
      title: 'Small groups, not a crowd',
      body: 'Circles give you a few nearby people to warm up with over a few days.',
      glyph: 'noCrowd' as const,
    },
    {
      title: 'Better ways to reply',
      body: 'Kynmarks offer light conversation cues, so replies feel less like guessing.',
      glyph: 'noDm' as const,
    },
    {
      title: 'Mutual when it matters',
      body: 'Friendship or dating only continues when both people choose each other and the lane lines up.',
      glyph: 'mutual' as const,
    },
  ] as const,
} as const;

/** “Why conversation first” — marketing homepage band (after How it works). */
export const conversationFirstSection = KYNWARD_MARKETING_CONVERSATION_FIRST;

/** @deprecated Use {@link conversationFirstSection} */
export const connectionFirstSection = conversationFirstSection;

/** @deprecated Use {@link conversationFirstSection} */
export const friendshipFirstSection = conversationFirstSection;

/** Reassurance after the circle: progress you keep, not a disposable loop */
export const whenCircleEnds = {
  h2: 'Kindle a connection',
  body: `After a Circle ends, choose anyone you’d be open to hearing from.\n\nThey’ll only know if they choose you too. Private chats open only when it’s mutual — no pressure either way.`,
} as const;

/** Replaces the old “beats / screenshots” block — no placeholders */
export const experienceSection = {
  h2: 'The Kynward loop',
  lines: [
    'See what’s forming nearby',
    'Join a guided Circle before your night',
    'Kindle mutual connections as Mutuals',
  ] as const,
  closer: 'A calmer path into nearby connection — from first prompt to mutual kindle.',
} as const;

/**
 * Safety, stated as the thing it prevents rather than as a category.
 *
 * "Safety by design" over a feature checklist is a label, not an answer: the reader who reaches
 * this section has a specific fear (the message they did not ask for, the meetup that goes wrong),
 * and a category heading does not touch it.
 *
 * CLAIMS: the headline is deliberately about PRIVATE CHATS, not messages in general — a Signal
 * Fire Fireside does put a stranger's opening line in front of you, so "nobody can ever message
 * you" would be false. A one-to-one opening only on mutual choice is true everywhere: Circle wrap
 * picks and a Signal Fire Kindle both need both sides.
 */
export const weCare = {
  h2: 'A private chat only ever opens because you chose it.',
  body: 'There is no open inbox here and no thread a stranger can start. *A private chat opens only when two people have each chosen the other,* and passing stays private, so nobody is handed a no.\n\nYou decide what you share and who can reach you. Reporting and blocking are always there — and if you meet someone in person, you can *name a contact we email if you never check in.*',
  learnMoreLabel: marketingCta.learnMoreSafety,
  learnMoreHref: `#${marketingAnchors.safety}`,
  checklist: [
    { label: 'Photo-verified profiles', glyph: 'shield' as const },
    { label: 'Meetup safety check-ins', glyph: 'lock' as const },
    { label: 'Profile checks', glyph: 'shield' as const },
    { label: 'Circle moderation', glyph: 'circle' as const },
    { label: 'Privacy controls', glyph: 'lock' as const },
    { label: 'Mutual-only chats', glyph: 'spark' as const },
  ] as const,
  items: [
    {
      label: 'Guided groups',
      body: 'Every Circle stays limited on purpose, so it’s easier to join in.',
      glyph: 'circle' as const,
    },
    {
      label: 'Clear ending',
      body: 'Circles run a few days — enough time to connect, not an endless thread.',
      glyph: 'clock' as const,
    },
    {
      label: 'Report and block',
      body: 'If something feels off, you’re in control — not stuck.',
      glyph: 'shield' as const,
    },
    {
      label: 'Mutual private chats',
      body: 'One-to-one chats unlock from mutual kindles, not a cold open.',
      glyph: 'lock' as const,
    },
  ] as const,
  closer: 'Attraction is normal. Pressure is not.',
} as const;

export const narrowDoor = {
  h2: 'We’re opening carefully',
  body: `We’re rolling out city by city so Circles feel full and local from day one. Join the waitlist and we’ll email you when Kynward opens where you are.`,
  noInvite: 'Not live in your city yet? Join the waitlist below.',
} as const;

/** Public launch waitlist (POST /api/waitlist/full-release) — not private beta. */
export const fullReleaseWaitlist = {
  h2: 'Join the Kynward waitlist',
  sub: 'We’ll email you once when Kynward opens in your area.',
  badge: 'Opening city by city',
  submitLabel: 'Join the waitlist',
  emailLabel: 'Email',
  firstNameLabel: 'First name (optional)',
  // Required now, and a canonical choice rather than free text — the launch email can only be sent
  // to a metro it can group, so a signup with no metro cannot be invited by the only mechanism that
  // will ever invite anyone.
  cityLabel: 'Your city',
  cityPlaceholder: 'Choose your city',
  cityOtherPlaceholder: 'Which city?',
  socialProofNearCta: 'No spam — just one email when we launch.',
  successNew: "You're on the list. We'll email you when Kynward opens.",
  successAgain: "You're already on the list — we'll email you at launch.",
  referralHeadline: "Know someone who'd love this?",
  // No "skip the line": launch notification is ORDER BY id ASC over unnotified rows, and nothing
  // reads a referral. A reward with no mechanism is the same dead-signal shape this page keeps
  // paying for — do not restore it until something actually reorders the queue.
  referralBody: 'Kynward opens city by city, so more people in yours is what moves it up the list.',
  referralCta: 'Copy invite link',
  referralCopied: 'Copied!',
  errorGeneric: 'Something went wrong. Please try again in a moment.',
  disabledHint:
    'Waitlist signups are unavailable in this build. If you’re the site owner, set VITE_MARKETING_BACKEND_URL for the marketing build.',
} as const;

export const faqSection = {
  h2: 'Questions we hear a lot',
  intro:
    'Still deciding if it’s for you? Here are the questions people usually ask before joining.',
} as const;

/**
 * The share band. The page had no share affordance at all — a reader who liked it could do nothing
 * but copy the URL out of the address bar, so every post driving traffic here hit a page that could
 * not propagate.
 *
 * The reason to share is the rollout, and it is TRUE rather than a growth-hack dressed as one:
 * Kynward opens one metro at a time, and which metro opens next is decided by where people are. It
 * deliberately promises no queue-jump, no reward and no referral credit — the last version of that
 * ("help them skip the line") had no reader and was removed; see the note on `referralBody`.
 */
export const shareSection = {
  h2: 'Know someone in your city who needs this?',
  body: 'Kynward opens one city at a time, and the next one is wherever enough people are waiting. Sending this to one person in your city does more than sending it to ten anywhere else.',
  shareCta: 'Share',
  copyCta: 'Copy link',
  copiedLabel: 'Link copied',
  shareTitle: 'Kynward',
  /** Used as the native share-sheet body and the X/WhatsApp prefill. Kept short: it sits in front of
   *  a link preview that already carries the headline and the card. */
  shareText:
    'Making friends shouldn’t be this hard. Neither should dating. Kynward puts you in a small Circle of nearby people — opening city by city.',
} as const;

export const finalCta = {
  h2: KYNWARD_MAIN_POSITIONING,
  // "This week", not "tonight": placement is the first drop your city can fill, and the drop band
  // above says so ("a day or two, not tonight"). The hero's own cadence note applies here too.
  // "Keep", not "Kindle" — this is the last line a cold reader sees and the verb is in-app jargon.
  sub: 'Pick a weekly night. A small Circle forms before it, and ends at a meetup the group picks. Keep the ones you click with.',
  brandLine: KYNWARD_POSITIONING_ANCHOR,
} as const;

export const marketingFooter = {
  tag: 'Pick a night. Meet a few people before it.',
  columns: [
    {
      title: 'Product',
      links: [
        { label: 'How it works', anchor: marketingAnchors.how },
        { label: 'Why it works', anchor: marketingAnchors.whyItWorks },
        { label: 'Meet-ups', anchor: marketingAnchors.meetups },
        { label: 'Circles', anchor: marketingAnchors.circles },
        { label: 'Signal Fire', anchor: marketingAnchors.signalFire },
        /* Mutuals is explained at the end of the Circle walkthrough now, not in its own section. */
        { label: 'Mutuals', anchor: marketingAnchors.circles },
      ],
    },
    {
      title: 'Trust',
      links: [
        { label: 'Safety', anchor: marketingAnchors.safety },
        { label: 'Safety Policy', href: '/legal/safety-policy' },
        { label: 'Community Guidelines', href: '/legal/community-guidelines' },
        { label: 'Privacy', href: '/legal/privacy-policy' },
        { label: 'Terms', href: '/legal/terms-of-service' },
        { label: 'Licenses', href: '/legal/licenses' },
      ],
    },
    {
      title: 'Connect',
      links: [
        { label: 'Join the waitlist', anchor: marketingAnchors.waitlist },
        { label: 'About', href: '/about' },
        { label: 'Contact', href: 'mailto:support@kynward.com' },
        { label: 'For venues', href: '/partners' },
        { label: 'Instagram', href: 'https://instagram.com/kynwardapp' },
      ],
    },
    {
      title: 'Made for you',
      links: [
        { label: 'For him', href: '/for-him' },
        { label: 'For her', href: '/for-her' },
        { label: 'Open-minded', href: '/open-minded' },
      ],
    },
  ] as const,
} as const;
